// Landing a search result on its exact match inside a rendered guide.
// The backend numbers a body hit by its occurrence in the guide's plain text
// (tags stripped, a space between blocks, whitespace collapsed, case-insensitive);
// this walks the rendered body the same way so the Nth occurrence here is the Nth there.

const BLOCKS = 'p, li, td, th, h1, h2, h3, h4, h5, h6, aside, figure';
/** Marks a character the walk inserted between two blocks — it has no source text. */
const BETWEEN_BLOCKS = -1;

interface IIndexedText {
  text: string;
  nodes: Text[];
  offsets: number[];
}

function indexText(root: HTMLElement): IIndexedText {
  const out: IIndexedText = { text: '', nodes: [], offsets: [] };
  const push = (character: string, node: Text, offset: number) => {
    out.text += character;
    out.nodes.push(node);
    out.offsets.push(offset);
  };
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let prevBlock: Element | null = null;
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const block = node.parentElement!.closest(BLOCKS);
    if (block !== prevBlock && out.text !== '' && !out.text.endsWith(' ')) push(' ', node, BETWEEN_BLOCKS);
    prevBlock = block;
    for (let i = 0; i < node.data.length; i++) {
      const space = /\s/.test(node.data[i]!);
      if (space && (out.text === '' || out.text.endsWith(' '))) continue;
      push(space ? ' ' : node.data[i]!.toLowerCase(), node, i);
    }
  }
  return out;
}

function unwrap(mark: HTMLElement): void {
  const parent = mark.parentNode!;
  parent.replaceChild(document.createTextNode(mark.textContent!), mark);
  parent.normalize();
}

/**
 * Wrap the `occurrence`-th match of `query` under `root` in plain spans — one
 * per text node it spans ("Trade <strong>Matrix</strong>" is two) — and return
 * them, first first, or null when there is no such match. Nothing shows until
 * `flashMarks` plays them.
 */
export function wrapMatch(root: HTMLElement, query: string, occurrence: number): HTMLElement[] | null {
  const needle = query.trim().toLowerCase().replace(/\s+/g, ' ');
  if (needle === '') return null;
  const { text, nodes, offsets } = indexText(root);
  let at = text.indexOf(needle);
  for (let seen = 0; at >= 0 && seen < occurrence; seen++) at = text.indexOf(needle, at + needle.length);
  if (at < 0) return null;

  const spans = new Map<Text, [number, number]>();
  for (let i = at; i < at + needle.length; i++) {
    if (offsets[i] === BETWEEN_BLOCKS) continue;
    const span = spans.get(nodes[i]!);
    spans.set(nodes[i]!, [span ? span[0] : offsets[i]!, offsets[i]! + 1]);
  }
  return [...spans].map(([node, [start, end]]) => {
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, end);
    const mark = document.createElement('span');
    range.surroundContents(mark);
    return mark;
  });
}

/** Play the jump flash on a match's marks; each unwraps itself when it ends, leaving the text as it was. */
export function flashMarks(marks: HTMLElement[], className: string): void {
  for (const mark of marks) {
    mark.className = className;
    mark.addEventListener('animationend', () => unwrap(mark), { once: true });
  }
}

/** Take back a match's marks that are still in place (a flashed one may already have unwrapped itself). */
export function clearMarks(marks: HTMLElement[]): void {
  for (const mark of marks) if (mark.parentNode) unwrap(mark);
}

/** Play the jump flash on a whole element, restarting it if it is already playing. */
export function flashElement(element: HTMLElement, className: string): void {
  const classes = className.split(' ');
  element.classList.remove(...classes);
  void element.offsetWidth; // reflow, so a repeat jump replays the animation
  element.classList.add(...classes);
  element.addEventListener('animationend', () => element.classList.remove(...classes), { once: true });
}
